import {
    AxisScale,
    ChartRange,
    LinePlotCurveStyle,
    Point,
    TimeSeriesChartPin,
    TimeSeriesChartSeriesDataPoint,
    TimeSeriesDataPoint,
    TimeSeriesMappedPin,
} from '@chart/core/types';
import { Bounds } from '@chart/shared/Bounds';
import { isChartRangeEmpty, mapDataPoint, mapValue } from '@chart/shared/data';
import { getLinePath } from '@chart/shared/path';
import { round } from '@sdweb/utils/number';
import { format, startOfDay, startOfHour, startOfMinute, startOfYear } from 'date-fns';

export const TimeSeriesChartYAxisSizePx = 65;
export const TimeSeriesChartXAxisSizePx = 20;
export const TimeSeriesChartPaddingPx = 5;
export const TimeSeriesChartTopPaddingPx = 10;

export const SelectedSeriesShadingOpacity = 0.25;
export const TimeSeriesXTickMinSpacingPx = 100;
export const TimeSeriesYTickMinSpacingPx = 30;
export const DimmedSeriesOpacity = 0.4;
export const MinMaxPlotOpacity = 0.4;
export const TimeSeriesZoneOpacity = 0.25;

export const formatTimestamp = (timestamp: number, dateFormat: string) =>
    format(new Date(timestamp), dateFormat);

export const xAxisTickLabelFormatter = (timestamp: number) => {
    if (timestamp === startOfYear(timestamp).getTime()) {
        return formatTimestamp(timestamp, 'MMM yyyy');
    }

    if (timestamp === startOfDay(timestamp).getTime()) {
        return formatTimestamp(timestamp, 'MMM dd');
    }

    if (timestamp === startOfHour(timestamp).getTime()) {
        return formatTimestamp(timestamp, 'hh a');
    }

    if (timestamp === startOfMinute(timestamp).getTime()) {
        return formatTimestamp(timestamp, 'mm:ss');
    }

    return formatTimestamp(timestamp, ':ss');
};

/**
 * The maximum number of characters in a trend tick value, not including the `-`
 * sign
 */
const MaxTickCharacters = 6;
export const yAxisTickLabelFormatter = (value: number) => {
    if (value === 0) {
        return '0';
    }
    const absVal = Math.abs(value);
    const sign = Math.sign(value);
    // in the case of 0.123, we still display the '0' char
    const charsInIntPart = absVal < 1 ? 1 : Math.floor(Math.log10(absVal) + 1);
    const decimalChar = 1;
    const decimalsToRoundTo = MaxTickCharacters - decimalChar - charsInIntPart;
    const roundedNum =
        decimalsToRoundTo > 0 ? round(absVal, decimalsToRoundTo) : Math.round(absVal);
    return (sign * roundedNum).toString();
};

export type MapTimeSeriesPinOptions = Readonly<{
    pins: TimeSeriesChartPin[];
    xAxisRange: ChartRange;
    leftYAxisRange?: ChartRange;
    rightYAxisRange?: ChartRange;
    width: number;
    height: number;
}>;

export const mapTimeSeriesPin = ({
    pins,
    xAxisRange,
    leftYAxisRange,
    rightYAxisRange,
    width,
    height,
}: MapTimeSeriesPinOptions): TimeSeriesMappedPin[] =>
    pins.map<TimeSeriesMappedPin>((pin) => {
        const yAxisRange = pin.axis === 'right' ? rightYAxisRange : leftYAxisRange;

        if (!yAxisRange) {
            throw new Error('Missing y axis configuration');
        }

        return {
            pin,
            x: mapValue(pin.pinPoint.x, xAxisRange.min, xAxisRange.max, 0, width),
            y: mapValue(pin.pinPoint.y, yAxisRange.min, yAxisRange.max, height, 0),
        };
    });

export type MapPointToAxisValuesOptions = Readonly<{
    point: Point;
    bounds: Bounds;
    leftAxisZoom: ChartRange;
    rightAxisZoom?: ChartRange;
    bottomAxisZoom: ChartRange;
}>;

export const mapPointToAxisValues = ({
    point,
    bounds,
    leftAxisZoom,
    rightAxisZoom,
    bottomAxisZoom,
}: MapPointToAxisValuesOptions) => ({
    x: mapValue(point.x, 0, bounds.width, bottomAxisZoom.min, bottomAxisZoom.max),
    leftY: mapValue(point.y, bounds.height, 0, leftAxisZoom.min, leftAxisZoom.max),
    rightY: rightAxisZoom
        ? mapValue(point.y, bounds.height, 0, rightAxisZoom.min, rightAxisZoom.max)
        : undefined,
});

export type FindClosestPointsOptions = Readonly<{
    point: Point;
    bounds: Bounds;
    seriesDataPoints: TimeSeriesChartSeriesDataPoint[];
    leftAxisZoom: ChartRange;
    leftAxisScale?: AxisScale;
    rightAxisZoom?: ChartRange;
    rightAxisScale?: AxisScale;
    bottomAxisZoom: ChartRange;
    bottomAxisScale: AxisScale;
}>;

type ReduceResult = Readonly<{
    distance: number;
    seriesDataPoints: TimeSeriesChartSeriesDataPoint[];
}>;

export const findClosestPoints = ({
    point,
    bounds,
    seriesDataPoints,
    leftAxisZoom,
    leftAxisScale,
    rightAxisZoom,
    rightAxisScale,
    bottomAxisZoom,
    bottomAxisScale,
}: FindClosestPointsOptions): TimeSeriesChartSeriesDataPoint[] => {
    if (isChartRangeEmpty(bottomAxisZoom)) {
        return [];
    }

    const { width, height } = bounds;

    return (
        seriesDataPoints.reduce<ReduceResult | undefined>((result, seriesDataPoint) => {
            const yAxisZoom =
                seriesDataPoint.series.axis === 'right' ? rightAxisZoom : leftAxisZoom;

            if (yAxisZoom && isChartRangeEmpty(yAxisZoom)) {
                return result;
            }

            if (!yAxisZoom) {
                throw new Error(
                    `Missing ${seriesDataPoint.series.axis} axis configuration`,
                );
            }

            const yAxisScale =
                seriesDataPoint.series.axis === 'right' ? rightAxisScale : leftAxisScale;

            if (!yAxisScale) {
                throw new Error(`Missing ${seriesDataPoint.series.axis} axis scale`);
            }

            const { dataPoint } = seriesDataPoint;
            const { x: mappedX, y: mappedY } = mapDataPoint({
                width,
                height,
                dataPoint,
                xAxisRange: bottomAxisZoom,
                xAxisScale: bottomAxisScale,
                yAxisRange: yAxisZoom,
                yAxisScale,
            });

            if (mappedX < 0 || mappedY < 0 || mappedX > width || mappedY > height) {
                return result;
            }

            const finalMappedX = mappedX + bounds.left;
            const finalMappedY = mappedY + bounds.top;

            const distance =
                (finalMappedX - point.x) ** 2 + (finalMappedY - point.y) ** 2;

            if (result === undefined || distance < result.distance) {
                return { distance, seriesDataPoints: [seriesDataPoint] };
            }

            if (distance === result.distance) {
                return {
                    ...result,
                    seriesDataPoints: [...result.seriesDataPoints, seriesDataPoint],
                };
            }

            return result;
        }, undefined)?.seriesDataPoints ?? []
    );
};

export type GetMinMaxLinePathOptions = Readonly<{
    dataPoints: TimeSeriesDataPoint[];
    width: number;
    height: number;
    xAxisRange: ChartRange;
    xAxisScale: AxisScale;
    yAxisRange: ChartRange;
    yAxisScale: AxisScale;
    curveStyle: LinePlotCurveStyle;
}>;

export const getMinMaxLinePath = ({
    dataPoints,
    width,
    height,
    xAxisRange,
    xAxisScale,
    yAxisRange,
    yAxisScale,
    curveStyle,
}: GetMinMaxLinePathOptions) => {
    const maxDataPoints = dataPoints.map(({ x, maxY: y }) =>
        mapDataPoint({
            width,
            height,
            dataPoint: { x, y },
            xAxisRange,
            xAxisScale,
            yAxisRange,
            yAxisScale,
        }),
    );

    const minDataPoints = dataPoints
        .map(({ x, minY: y }) =>
            mapDataPoint({
                width,
                height,
                dataPoint: { x, y },
                xAxisRange,
                xAxisScale,
                yAxisRange,
                yAxisScale,
            }),
        )
        .reverse();

    const linePath = getLinePath({
        dataPoints: [...maxDataPoints, ...minDataPoints],
        curveStyle,
    });

    return `${linePath}z`;
};
